import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, Button } from 'reactstrap';
import { useAlertPlus } from '../../../hooks';
import AlertModal from '../../../components/AlertModal';
import Form from './Form';
import config from 'config';
import { ApiService } from '../../../services';
import { getPreviewSrc, encodeFileName } from '../../../helpers/filesPreview';

import './Edit.scss';

export interface MatriculaRequest {
  id: string | undefined;
  solicitud_id: string;
  nombre: string;
  apellido: string;
  dni: string;
  cuit: string;
  sexo: string;
  localidad_nac: string;
  partido_nac: string;
  provincia_nac: string;
  pais_nac: string;
  pais_nac_id: number,
  fecha_nac: string;
  prefijo_telefono: string;
  telefono: string;
  email: string;

  delegacion_nro: string;
  delegacion_localidad: string;

  particular_calle: string;
  particular_numero: string;
  particular_piso: string;
  particular_dpto: string;
  particular_localidad: string;
  particular_partido: string;
  particular_provincia: string;
  particular_codigo_postal: string;
  particular_prefijo_telefono: string;
  particular_telefono: string;

  legal_calle: string;
  legal_numero: string;
  legal_piso: string;
  legal_dpto: string;
  legal_localidad: string;
  legal_partido: string;
  legal_provincia: string;
  legal_codigo_postal: string;
  legal_prefijo_telefono: string;
  legal_telefono: string;

  titulo_institucion: string;
  titulo_nombre: string;
  titulo_fecha: string;

  forma_pago: string;
  tipo_tarjeta: string | null;
  numero_tarjeta_cbu: string;

  motivo_rechazo: string;

  state_id: string;

  attachs: RequestAttachment[];

  solicitud_ck?: {
    estado_solicitud_ck?: string;
  }

  matricula_ck?: {
    MTR001_N_TOMO?: string;
    MTR001_N_FOLIO?: string;
  }
}

export interface GalleryItem {
  src: string;
  downloadUrl: string;
  alt: string;
}

export interface RequestAttachment {
  id: number;
  request_id: number;
  request_type: string;
  attachment_type: string;
  url: string;
  created_at: string;
}

function Edit() {
  const { id } = useParams();

  const [data, setData] = useState<MatriculaRequest>({
    id: id,
    solicitud_id: '',
    nombre: '',
    apellido: '',
    dni: '',
    cuit: '',
    sexo: '',
    localidad_nac: '',
    partido_nac: '',
    provincia_nac: '',
    pais_nac: '',
    pais_nac_id: 0,
    fecha_nac: '',
    prefijo_telefono: '',
    telefono: '',
    email: '',

    delegacion_nro: '',
    delegacion_localidad: '',

    particular_calle: '',
    particular_numero: '',
    particular_piso: '',
    particular_dpto: '',
    particular_localidad: '',
    particular_partido: '',
    particular_provincia: '',
    particular_codigo_postal: '',
    particular_prefijo_telefono: '',
    particular_telefono: '',

    legal_calle: '',
    legal_numero: '',
    legal_piso: '',
    legal_dpto: '',
    legal_localidad: '',
    legal_partido: '',
    legal_provincia: '',
    legal_codigo_postal: '',
    legal_prefijo_telefono: '',
    legal_telefono: '',

    titulo_institucion: '',
    titulo_nombre: '',
    titulo_fecha: '',

    forma_pago: '',
    tipo_tarjeta: '',
    numero_tarjeta_cbu: '',

    motivo_rechazo: '',

    state_id: '',

    attachs: [],
  });

  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const galleryContainer = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [alertShow, alertMessage, alertCallback, alert] = useAlertPlus('');

  const handleSend = async (accept = false) => {
    try {
      setIsLoading(true);

      // Preparamos los datos
      const formData = new FormData();
      formData.append('motivo_rechazo', data.motivo_rechazo);

      const headers: HeadersInit = {
        ...(localStorage.getItem('accessToken') && {
          Authorization: localStorage.getItem('accessToken') as string,
        }),
      };

      const res = await fetch(`${config.baseUrl}/matriculates/matriculas/${ id }/${ accept ? 'accept' : 'reject' }`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const response = await res.json();

      if (response?.status !== 'ok') {
        console.info(response);

        if (response.payload?.message) {
          alert(response.payload.message);
          return;
        }

        alert('Ha ocurrido un error al enviar al servidor, por favor intente nuevamente.');
        return;
      }

      alert('Se guardó correctamente la resolución.', () => {
        window.location.href = '/admin/matriculates/requests';
      });
    } catch (err) {
      console.error(err);
      alert('Ha ocurrido un error al guardar la resolución, por favor intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const ATTACHMENTS_CONFIG = [
    { key: 'titulo_adjunto', alt: 'Título Universitario' },
    { key: 'foto_carnet', alt: 'Foto Carnet' },
    { key: 'fotocopia_dni_frente', alt: 'Fotocopia DNI Frente' },
    { key: 'fotocopia_dni_dorso', alt: 'Fotocopia DNI Dorso' },
    { key: 'antecedentes_penales', alt: 'Antecedentes Penales' },
    { key: 'certificado_extranjero', alt: 'Certificado Extranjero' },
  ] as const;

  useEffect(() => {
    ApiService.getOne('matriculates/matriculas', id)
      .then(({ payload }) => {
        setData(payload);

        const attachs = ATTACHMENTS_CONFIG.flatMap(({ key, alt }) => {
          if (!payload.attachs?.length) return [];

          const filtered = payload.attachs
            .filter((a: RequestAttachment) => a.attachment_type.startsWith(key))
            .sort((a: RequestAttachment, b: RequestAttachment) => {
              const indexA = parseInt(a.attachment_type.split('_').pop() || '0');
              const indexB = parseInt(b.attachment_type.split('_').pop() || '0');
              return indexA - indexB;
            });

          let i = 0;
          return filtered.map((a: RequestAttachment) => {
            const encodedPath = encodeFileName(a.url);
            i++;

            return {
              src: getPreviewSrc(encodedPath),
              downloadUrl: `${config.baseUrl}/${encodedPath}`,
              // @ts-ignore
              alt: `${alt} ${i}`,
            };
          });
        });

        setGallery(attachs);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body"></div>
        </Container>
      </div>
      <Container className="mt--7" fluid>
        <Card className="bg-secondary shadow">
          <CardHeader>
            <Row>
              <Col md="8">
                <h3 className="mb-0">
                  Solicitud de Matrícula #{ data.solicitud_id }
                  { data.solicitud_ck?.estado_solicitud_ck
                    ? ` - ${data.solicitud_ck?.estado_solicitud_ck}`
                    : null
                  }
                </h3>
              </Col>
              <Col md="4" className="text-right">
                <Link to='/admin/matriculates/requests'>
                  <Button color="primary" size="md">Volver</Button>
                </Link>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Form
              data={ data }
              setData={ setData }
              gallery={ gallery }
              galleryContainer={ galleryContainer }
              isLoading={ isLoading }
              onAccept={ () => handleSend(true) }
              onReject={ () => handleSend(false) }
              onCreateMatricula={ false }
            />
          </CardBody>
        </Card>
      </Container>
      <AlertModal isOpen={ alertShow } message={ alertMessage } onClose={ alertCallback } />
    </>
  );
}

export default Edit;
